import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Menu,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HistoryIcon from "@mui/icons-material/History";
import { ajustarTamanhoFonteSre } from "../../estilos/global";

const azul = "#176fb2";

function formatarData(valor, completa = false) {
  if (!valor) return "";

  const data = new Date(valor);

  if (Number.isNaN(data.getTime())) return "";

  const agora = new Date();

  const inicioHoje = new Date(
    agora.getFullYear(),
    agora.getMonth(),
    agora.getDate(),
  );

  const inicioOntem = new Date(inicioHoje);

  inicioOntem.setDate(inicioOntem.getDate() - 1);

  const hora = data.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (!completa && data >= inicioHoje) return `Hoje, ${hora}`;
  if (!completa && data >= inicioOntem) return `Ontem, ${hora}`;

  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function dadosCadastro(item) {
  if (
    !["cadastro_pendente", "cadastro_aprovado", "cadastro_recusado"].includes(
      item.tipo,
    )
  ) {
    return null;
  }

  const mensagem = item.mensagem || "";

  const cadastro = /O cadastro de (mantenedora|escola) [“"](.+?)[”"]/iu.exec(
    mensagem,
  );

  const cadastroComCidade =
    /^[“"](.+?)[”"], de (.+?)\/([A-Z]{2}), aguarda análise\.?$/iu.exec(
      mensagem,
    );

  const tipo = cadastro?.[1] || item.origem_tipo;

  if (!["mantenedora", "escola"].includes(tipo)) return null;

  let status = "pendente";

  if (item.tipo === "cadastro_aprovado") status = "aprovada";
  if (item.tipo === "cadastro_recusado") status = "recusada";

  if (item.tipo === "cadastro_pendente" && item.resolvida_em) {
    if (/aprovad[oa]/i.test(item.resultado || "")) status = "aprovada";
    if (/recusad[oa]/i.test(item.resultado || "")) status = "recusada";
  }

  const analistaDaMensagem = /\bpor (.+?)\.?$/iu.exec(mensagem)?.[1];

  const analistaDoResultado = /\bpor (.+?)\.?$/iu.exec(
    item.resultado || "",
  )?.[1];

  return {
    tipo,
    nome: item.cadastro_nome || cadastro?.[2] || cadastroComCidade?.[1] || null,
    localidade: cadastroComCidade
      ? `${cadastroComCidade[2]}/${cadastroComCidade[3]}`
      : null,
    status,
    analista:
      item.resolvida_por_nome ||
      (item.tipo !== "cadastro_pendente" ? item.ator_nome : null) ||
      analistaDaMensagem ||
      analistaDoResultado ||
      null,
    dataAcao: formatarData(
      item.tipo === "cadastro_pendente" ? item.resolvida_em : item.acao_em,
      true,
    ),
    motivo: status === "recusada" ? item.motivo : null,
  };
}

function dadosProcesso(item) {
  const mensagem = item.mensagem || "";
  const resultado = item.resultado || "";

  if (item.tipo === "analista_designado") {
    const id = Number(item.origem_id ?? item.processo_id);

    if (!Number.isInteger(id) || id <= 0) return null;

    return {
      nome: item.processo_nome || null,
      origemTipo: item.processo_origem_tipo || null,
      origemNome: item.processo_origem_nome || null,
      status: "atribuido",
      responsavel: item.ator_nome || null,
      dataAcao: formatarData(item.acao_em || item.criada_em, true),
    };
  }

  if (item.tipo === "processo_sem_atribuicao") {
    const autor =
      /^(.+?) desatribuiu o processo\.\s*/iu.exec(mensagem)?.[1] || null;

    const texto = mensagem.replace(/^.+? desatribuiu o processo\.\s*/iu, "");

    const dados =
      /^(.*?)\s+—\s+(Escola|Mantenedora):\s+(.+?)\. Processo nº (\d+) aguarda atribuição/u.exec(
        texto,
      );

    if (dados || item.processo_nome) {
      let status = autor ? "desatribuido" : "sem_atribuicao";
      let responsavel = autor;

      if (item.resolvida_em && /rejeitad|excluíd/i.test(resultado)) {
        status = "rejeitado";
        responsavel =
          item.resolvida_por_nome ||
          /\bpor (.+?)\.?$/iu.exec(resultado)?.[1] ||
          autor;
      } else if (item.resolvida_em && /atribuíd|atribuido/i.test(resultado)) {
        status = "atribuido";
        responsavel =
          item.resolvida_por_nome ||
          /\ba (.+?)\.?$/iu.exec(resultado)?.[1] ||
          autor;
      }

      return {
        titulo: autor
          ? "Esse processo foi desatribuído"
          : "Chegou um novo processo",
        nome: item.processo_nome || dados?.[1] || null,
        origemTipo: item.processo_origem_tipo || dados?.[2] || null,
        origemNome: item.processo_origem_nome || dados?.[3] || null,
        status,
        responsavel,
      };
    }
  }

  if (item.tipo === "processo_atribuido_geral") {
    const dados =
      /^O processo (\d+) \((.+?)\) foi atribuído a (.+?)\. (Escola|Mantenedora): (.+?)\.$/u.exec(
        mensagem,
      );

    if (dados || item.processo_nome) {
      return {
        titulo: "Chegou um novo processo",
        nome: item.processo_nome || dados?.[2] || null,
        responsavel: dados?.[3] || item.ator_nome || null,
        origemTipo: item.processo_origem_tipo || dados?.[4] || null,
        origemNome: item.processo_origem_nome || dados?.[5] || null,
        status: "atribuido",
      };
    }
  }

  if (item.tipo === "processo_atribuido") {
    const nome = /^O processo [“"](.+)[”"] foi atribuído a você\.?$/u.exec(
      mensagem,
    )?.[1];

    return {
      nome: item.processo_nome || nome || null,
      origemTipo: item.processo_origem_tipo || null,
      origemNome: item.processo_origem_nome || null,
      status: "atribuido",
      responsavel: null,
    };
  }

  if (item.tipo === "processo_rejeitado") {
    const dados =
      /^Processo (\d+) \((.+?)\) rejeitado por (.+?)\. (Escola|Mantenedora): (.+?)\.?$/u.exec(
        mensagem,
      );

    if (dados) {
      return {
        nome: dados[2],
        responsavel: dados[3],
        origemTipo: dados[4],
        origemNome: dados[5].replace(/\.$/, ""),
        status: "rejeitado",
      };
    }
  }

  if (item.tipo === "documento_devolvido") {
    return {
      nome: item.processo_nome || null,
      origemTipo: item.processo_origem_tipo || null,
      origemNome: item.processo_origem_nome || null,
      documento: item.documento_nome || null,
      motivo: item.motivo || null,
    };
  }

  if (item.tipo === "documento_reenviado") {
    const dados =
      /^[“"](.+?)[”"] foi reenviado\. Processo [“"](.+?)[”"](?: nº \d+)?\. (Escola|Mantenedora): (.+?)\.$/u.exec(
        mensagem,
      );

    if (dados) {
      return {
        documento: dados[1],
        nome: dados[2],
        origemTipo: dados[3],
        origemNome: dados[4],
      };
    }
  }

  return null;
}

function podeAbrir(item, perfil) {
  if (
    item.resolvida_em ||
    ["cadastro_recusado", "processo_rejeitado"].includes(item.tipo) ||
    !["analyst", "user"].includes(perfil)
  ) {
    return false;
  }

  if (["escola", "mantenedora"].includes(item.origem_tipo)) {
    if (perfil === "user" && item.tipo !== "cadastro_aprovado") {
      return false;
    }

    if (
      perfil === "analyst" &&
      !["cadastro_aprovado", "cadastro_pendente"].includes(item.tipo)
    ) {
      return false;
    }
  }

  const id = Number(
    item.origem_tipo === "documento"
      ? item.processo_id
      : (item.origem_id ?? item.processo_id),
  );

  return (
    Number.isInteger(id) &&
    id > 0 &&
    ["processo", "documento", "escola", "mantenedora"].includes(
      item.origem_tipo,
    )
  );
}

function rotuloDestino(item, perfil) {
  if (["processo", "documento"].includes(item.origem_tipo)) {
    return "Ver processo";
  }

  return perfil === "user" ? "Ver meus cadastros" : "Ver cadastro";
}

function simbolo(item) {
  if (item.origem_tipo === "mantenedora") {
    return <BusinessIcon sx={{ fontSize: 19 }} />;
  }

  if (item.origem_tipo === "escola") {
    return <SchoolOutlinedIcon sx={{ fontSize: 19 }} />;
  }

  return <DescriptionOutlinedIcon sx={{ fontSize: 19 }} />;
}

function normalizarNome(nome) {
  if (!nome) return "";

  const texto = String(nome).trim().replace(/\s+/g, " ");

  if (texto !== texto.toLocaleUpperCase("pt-BR")) {
    return texto;
  }

  const conectivos = new Set([
    "a",
    "o",
    "as",
    "os",
    "de",
    "da",
    "do",
    "das",
    "dos",
    "e",
    "ou",
    "em",
    "no",
    "na",
    "nos",
    "nas",
    "ao",
    "aos",
    "à",
    "às",
  ]);

  const siglas = new Set(["SEAR", "SRE", "MG", "EJA", "CNPJ"]);

  return texto
    .toLocaleLowerCase("pt-BR")
    .replace(/\p{L}+/gu, (palavra, posicao) => {
      const caixaAlta = palavra.toLocaleUpperCase("pt-BR");

      if (siglas.has(caixaAlta)) return caixaAlta;
      if (posicao > 0 && conectivos.has(palavra)) return palavra;

      return palavra.charAt(0).toLocaleUpperCase("pt-BR") + palavra.slice(1);
    });
}

function NomeComTooltip({ nome, discreto = false }) {
  if (!nome) return null;

  return (
    <Tooltip title={nome} arrow placement="top" enterTouchDelay={700}>
      <Typography
        component="span"
        tabIndex={0}
        aria-label={nome}
        sx={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          overflowWrap: "anywhere",
          minWidth: 0,
          color: discreto ? "#526b7c" : "#314e62",
          fontSize: discreto
            ? ajustarTamanhoFonteSre(12)
            : ajustarTamanhoFonteSre(13),
          fontWeight: discreto ? 500 : 700,
          lineHeight: 1.45,
          cursor: "default",
          "&:focus-visible": {
            outline: `2px solid ${azul}`,
            borderRadius: 0.5,
          },
        }}
      >
        {normalizarNome(nome)}
      </Typography>
    </Tooltip>
  );
}

function SeloStatus({ status }) {
  const aprovada = status === "aprovada";
  const recusada = status === "recusada";

  const cor = aprovada ? "#218052" : recusada ? "#ba4545" : azul;
  const fundo = aprovada ? "#e1f5e9" : recusada ? "#fde9e9" : "#e8f3fc";
  const rotulo = aprovada ? "Aprovada" : recusada ? "Recusada" : "Pendente";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        px: 0.7,
        py: 0.15,
        borderRadius: 5,
        bgcolor: fundo,
        color: cor,
        flexShrink: 0,
      }}
    >
      {aprovada ? (
        <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
      ) : recusada ? (
        <HighlightOffIcon sx={{ fontSize: 15 }} />
      ) : (
        <ScheduleOutlinedIcon sx={{ fontSize: 15 }} />
      )}

      <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.4 }}>
        {rotulo}
      </Typography>
    </Box>
  );
}

const estilosProcesso = {
  atribuido: {
    rotulo: "Atribuído",
    cor: "#23774e",
    fundo: "#e5f5ec",
    borda: "#b9dec8",
  },
  desatribuido: {
    rotulo: "Desatribuído",
    cor: "#99600b",
    fundo: "#fff0d9",
    borda: "#e9ca93",
  },
  sem_atribuicao: {
    rotulo: "Sem analista",
    cor: "#99600b",
    fundo: "#fff0d9",
    borda: "#e9ca93",
  },
  rejeitado: {
    rotulo: "Rejeitado",
    cor: "#26313b",
    fundo: "#26313b",
    borda: "#26313b",
  },
};

function SeloProcesso({ status }) {
  const estilo = estilosProcesso[status];

  if (!estilo) return null;

  const rejeitado = status === "rejeitado";

  const Icone = rejeitado
    ? DeleteOutlineIcon
    : status === "atribuido"
      ? CheckCircleOutlineIcon
      : ScheduleOutlinedIcon;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        px: 0.7,
        py: 0.15,
        borderRadius: 5,
        bgcolor: estilo.fundo,
        color: rejeitado ? "#fff" : estilo.cor,
        border: `1px solid ${estilo.borda}`,
      }}
    >
      <Icone sx={{ fontSize: 15 }} />

      <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.4 }}>
        {estilo.rotulo}
      </Typography>
    </Box>
  );
}

function LinhaInformacao({ rotulo, valor, ultima = false }) {
  if (!valor) return null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "74px minmax(0, 1fr)",
        columnGap: 0.75,
        alignItems: "start",
        py: 0.5,
        borderBottom: ultima ? "none" : "1px solid #e1eaf1",
      }}
    >
      <Typography variant="caption" sx={{ color: "#70869a", lineHeight: 1.45 }}>
        {rotulo}:
      </Typography>

      <NomeComTooltip nome={valor} discreto />
    </Box>
  );
}

export default function NotificationMenu({
  perfil,
  notifications = [],
  loading = false,
  error = "",
  onOpen,
  onRead,
  onReadAll,
  onClear,
  onView,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [aba, setAba] = useState("geral");
  const [historicoAberto, setHistoricoAberto] = useState(false);

  const [naoLidasAbertas, setNaoLidasAbertas] = useState({
    geral: true,
    individual: true,
  });

  const naoLidasAberto = naoLidasAbertas[aba];

  const naoLidas = notifications.filter(
    (item) => !item.lida && !item.resolvida_em,
  );

  const totalNaoLidas = naoLidas.length;

  const paraTodos = naoLidas.filter((item) => item.alcance === "geral").length;

  const paraMim = naoLidas.filter(
    (item) => item.alcance === "individual",
  ).length;

  const exibirAbas = perfil === "analyst";

  const daAba = exibirAbas
    ? notifications.filter((item) => item.alcance === aba)
    : notifications;

  const atuais = daAba.filter((item) => !item.lida && !item.resolvida_em);

  const historico = daAba.filter((item) => item.lida || item.resolvida_em);

  const abrirMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setAba(perfil === "user" ? "individual" : "geral");
    setHistoricoAberto(false);
    onOpen?.();
  };

  const visualizar = async (item) => {
    if (!podeAbrir(item, perfil) || !onView) return;

    const destino =
      item.origem_tipo === "documento"
        ? {
            ...item,
            origem_tipo: "processo",
            origem_id: item.processo_id,
          }
        : item;

    const abriu = await onView(destino);

    if (abriu) setAnchorEl(null);
  };

  const renderizarItem = (item, ehHistorico = false) => {
    const cadastro = dadosCadastro(item);
    const processo = dadosProcesso(item);
    const resolvida = Boolean(item.resolvida_em);
    const podeVisualizar = podeAbrir(item, perfil) && Boolean(onView);
    const estiloProcesso = processo?.status && estilosProcesso[processo.status];

    const titulo = cadastro
      ? `Cadastro de ${cadastro.tipo}`
      : processo?.titulo || item.titulo;

    const linhas = [];

    if (cadastro?.nome) {
      linhas.push({
        rotulo: cadastro.tipo === "escola" ? "Escola" : "Mantenedora",
        valor: cadastro.nome,
      });
    }

    if (cadastro?.localidade) {
      linhas.push({
        rotulo: "Cidade/UF",
        valor: cadastro.localidade,
      });
    }

    if (processo?.nome) {
      linhas.push({
        rotulo: "Processo",
        valor: processo.nome,
      });
    }

    if (processo?.origemNome) {
      linhas.push({
        rotulo: processo.origemTipo,
        valor: processo.origemNome,
      });
    }

    if (processo?.documento) {
      linhas.push({
        rotulo: "Documento",
        valor: processo.documento,
      });
    }

    if (linhas.length === 0) {
      linhas.push({
        rotulo: "Detalhes",
        valor: item.mensagem || item.titulo,
      });
    }

    return (
      <Box
        key={item.id}
        sx={{
          display: "flex",
          gap: 1.2,
          px: 1.3,
          py: 1.2,
          mb: 0.9,
          borderRadius: 1.5,
          bgcolor: ehHistorico ? "#fff" : "#f5faff",
          border: "1px solid #e2ebf3",
          borderLeft: ehHistorico ? "3px solid #c8d4de" : `3px solid ${azul}`,
          boxShadow: ehHistorico ? "none" : "0 2px 6px rgba(25, 67, 100, .06)",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: ehHistorico ? "#edf1f4" : "#e2f1ff",
            color: ehHistorico ? "#8494a1" : azul,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            mt: 0.2,
          }}
        >
          {simbolo(item)}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            <Box
              component="span"
              sx={{
                px: 0.8,
                py: 0.2,
                borderRadius: 5,
                bgcolor: ehHistorico ? "#edf1f4" : "#e2f1ff",
                color: ehHistorico ? "#607788" : azul,
                fontSize: ajustarTamanhoFonteSre(11),
                fontWeight: 700,
              }}
            >
              {exibirAbas
                ? item.alcance === "geral"
                  ? "Para todos"
                  : "Para você"
                : "Aviso"}
            </Box>

            <Typography variant="caption" sx={{ color: "#718699" }}>
              {cadastro?.dataAcao ||
                processo?.dataAcao ||
                formatarData(item.criada_em)}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.7,
              mt: 0.45,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: "#243a4b",
                overflowWrap: "anywhere",
              }}
            >
              {processo ? normalizarNome(titulo) : titulo}
            </Typography>

            {cadastro && <SeloStatus status={cadastro.status} />}

            {processo?.status && <SeloProcesso status={processo.status} />}
          </Box>

          {linhas.length > 0 ? (
            <Box sx={{ mt: 0.5 }}>
              {linhas.map((linha, indice) => (
                <LinhaInformacao
                  key={`${linha.rotulo}-${indice}`}
                  rotulo={linha.rotulo}
                  valor={linha.valor}
                  ultima={indice === linhas.length - 1}
                />
              ))}
            </Box>
          ) : (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.55,
                color: "#526b7c",
                overflowWrap: "anywhere",
                whiteSpace: "pre-line",
              }}
            >
              {item.mensagem}
            </Typography>
          )}

          {processo?.motivo && (
            <Box component="details" sx={{ mt: 0.7 }}>
              <Box
                component="summary"
                sx={{
                  width: "fit-content",
                  color: azul,
                  fontSize: ajustarTamanhoFonteSre(12),
                  fontWeight: 700,
                  cursor: "pointer",
                  borderRadius: 0.5,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${azul}`,
                    outlineOffset: 3,
                  },
                }}
              >
                Ver motivo da devolução
              </Box>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.55,
                  color: "#526b7c",
                  overflowWrap: "anywhere",
                  whiteSpace: "pre-wrap",
                }}
              >
                {processo.motivo}
              </Typography>
            </Box>
          )}

          {cadastro?.status !== "pendente" && cadastro?.analista && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.55,
                mt: 0.7,
                px: 0.8,
                py: 0.5,
                borderRadius: 1,
                bgcolor: cadastro.status === "aprovada" ? "#e1f5e9" : "#fde9e9",
                color: cadastro.status === "aprovada" ? "#218052" : "#ba4545",
              }}
            >
              <PersonOutlineIcon sx={{ fontSize: 16, flexShrink: 0 }} />

              <Typography
                variant="caption"
                sx={{ fontWeight: 700, overflowWrap: "anywhere" }}
              >
                Analista: {cadastro.analista}
              </Typography>
            </Box>
          )}

          {cadastro?.status === "recusada" && cadastro.motivo && (
            <Box component="details" sx={{ mt: 0.7 }}>
              <Box
                component="summary"
                sx={{
                  width: "fit-content",
                  color: azul,
                  fontSize: ajustarTamanhoFonteSre(12),
                  fontWeight: 700,
                  cursor: "pointer",
                  borderRadius: 0.5,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${azul}`,
                    outlineOffset: 3,
                  },
                }}
              >
                Ver motivo da recusa
              </Box>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.55,
                  color: "#526b7c",
                  overflowWrap: "anywhere",
                  whiteSpace: "pre-wrap",
                }}
              >
                {cadastro.motivo}
              </Typography>
            </Box>
          )}

          {processo &&
            !resolvida &&
            ["sem_atribuicao", "desatribuido"].includes(processo.status) && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  color: estiloProcesso.cor,
                  fontWeight: 700,
                }}
              >
                Aguarda atribuição a um analista.
              </Typography>
            )}

          {processo?.responsavel && estiloProcesso && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.55,
                mt: 0.7,
                px: 0.8,
                py: 0.5,
                borderRadius: 1,
                bgcolor:
                  processo.status === "rejeitado"
                    ? "#e8ecef"
                    : estiloProcesso.fundo,
                border: `1px solid ${
                  processo.status === "rejeitado"
                    ? "#c9d1d8"
                    : estiloProcesso.borda
                }`,
                color: estiloProcesso.cor,
              }}
            >
              <PersonOutlineIcon sx={{ fontSize: 16, flexShrink: 0 }} />

              <Typography
                variant="caption"
                sx={{ fontWeight: 700, overflowWrap: "anywhere" }}
              >
                {item.tipo === "analista_designado"
                  ? "Analista responsável:"
                  : processo.status === "rejeitado"
                    ? "Rejeitado por"
                    : processo.status === "desatribuido"
                      ? "Desatribuído por"
                      : "Atribuído por"}{" "}
                {normalizarNome(processo.responsavel)}
              </Typography>
            </Box>
          )}

          {resolvida && !cadastro && !processo?.status && item.resultado && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.5, color: "#348058" }}
            >
              {item.resultado}
            </Typography>
          )}

          {ehHistorico &&
            resolvida &&
            item.tipo === "processo_sem_atribuicao" &&
            processo?.status &&
            !["sem_atribuicao", "desatribuido"].includes(processo.status) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 0.6,
                  mt: 0.7,
                }}
              >
                <Typography variant="caption" sx={{ color: "#667a88" }}>
                  Status anterior:
                </Typography>

                <Box
                  sx={{
                    px: 0.7,
                    py: 0.15,
                    borderRadius: 5,
                    bgcolor: "#fff0d9",
                    color: "#99600b",
                    fontSize: ajustarTamanhoFonteSre(11),
                  }}
                >
                  Sem atribuição
                </Box>

                <ArrowForwardIcon sx={{ fontSize: 14, color: "#8797a5" }} />

                <Typography variant="caption" sx={{ color: "#667a88" }}>
                  Status atual:
                </Typography>

                <Box
                  sx={{
                    px: 0.7,
                    py: 0.15,
                    borderRadius: 5,
                    bgcolor: estiloProcesso.fundo,
                    color:
                      processo.status === "rejeitado"
                        ? "#fff"
                        : estiloProcesso.cor,
                    fontSize: ajustarTamanhoFonteSre(11),
                    fontWeight: 700,
                  }}
                >
                  {estiloProcesso.rotulo}
                </Box>
              </Box>
            )}

          {ehHistorico && cadastro && cadastro.status !== "pendente" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.6,
                mt: 0.7,
              }}
            >
              <Typography variant="caption" sx={{ color: "#667a88" }}>
                Status anterior:
              </Typography>

              <Box
                sx={{
                  px: 0.7,
                  py: 0.15,
                  borderRadius: 5,
                  bgcolor: "#edf1f4",
                  color: "#566d7f",
                  fontSize: ajustarTamanhoFonteSre(11),
                }}
              >
                Pendente
              </Box>

              <ArrowForwardIcon sx={{ fontSize: 14, color: "#8797a5" }} />

              <Typography variant="caption" sx={{ color: "#667a88" }}>
                Status atual:
              </Typography>

              <Box
                sx={{
                  px: 0.7,
                  py: 0.15,
                  borderRadius: 5,
                  bgcolor:
                    cadastro.status === "aprovada" ? "#e1f5e9" : "#fde9e9",
                  color: cadastro.status === "aprovada" ? "#218052" : "#ba4545",
                  fontSize: ajustarTamanhoFonteSre(11),
                  fontWeight: 700,
                }}
              >
                {cadastro.status === "aprovada" ? "Aprovada" : "Recusada"}
              </Box>
            </Box>
          )}

          {ehHistorico && item.lida && !resolvida && !cadastro && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.5, color: "#6d8190" }}
            >
              Lida • situação ainda em andamento
            </Typography>
          )}

          {(podeVisualizar || !item.lida) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 0.6,
                borderTop: "1px solid #e1eaf1",
                mt: 0.8,
                pt: 0.8,
              }}
            >
              {podeVisualizar ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => visualizar(item)}
                  endIcon={
                    <ChevronRightIcon sx={{ fontSize: "16px !important" }} />
                  }
                  sx={{
                    textTransform: "none",
                    fontSize: ajustarTamanhoFonteSre(11),
                    fontWeight: 700,
                    bgcolor: azul,
                    boxShadow: "none",
                  }}
                >
                  {rotuloDestino(item, perfil)}
                </Button>
              ) : (
                <Box />
              )}

              {!item.lida && (
                <Button
                  size="small"
                  startIcon={
                    <DoneAllIcon sx={{ fontSize: "16px !important" }} />
                  }
                  onClick={() => onRead?.(item.id)}
                  sx={{
                    p: 0,
                    minWidth: 0,
                    fontSize: ajustarTamanhoFonteSre(11),
                    textTransform: "none",
                    color: azul,
                  }}
                >
                  Marcar como lida
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={abrirMenu}
        aria-label={`Notificações: ${totalNaoLidas} não lidas`}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1,
          flexShrink: 0,
        }}
      >
        <Badge badgeContent={totalNaoLidas} color="error" max={99}>
          <NotificationsNoneIcon sx={{ fontSize: 26 }} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        keepMounted
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            width: {
              xs: "calc(100vw - 24px)",
              sm: 500,
            },
            maxWidth: 500,
            borderRadius: 2,
            overflow: "hidden",
            borderTop: `3px solid ${azul}`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            pt: 1,
            pb: 0.6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <NotificationsNoneIcon sx={{ color: azul, fontSize: 25 }} />

            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: "#233a4d",
                fontSize: ajustarTamanhoFonteSre(18),
              }}
            >
              Notificações
            </Typography>
          </Box>

          {totalNaoLidas > 0 && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 5,
                bgcolor: "#eaf4fc",
                color: azul,
                fontSize: ajustarTamanhoFonteSre(11),
                fontWeight: 700,
              }}
            >
              {totalNaoLidas} novas
            </Box>
          )}
        </Box>

        {totalNaoLidas > 0 && onReadAll && (
          <Button
            size="small"
            startIcon={<DoneAllIcon />}
            onClick={onReadAll}
            sx={{
              mx: 1.5,
              mb: 0.4,
              textTransform: "none",
              fontSize: ajustarTamanhoFonteSre(12),
              color: azul,
            }}
          >
            Marcar todas como lidas
          </Button>
        )}

        {exibirAbas && (
          <Tabs
            value={aba}
            onChange={(_, valor) => {
              setAba(valor);
              setHistoricoAberto(false);
            }}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              borderBottom: "1px solid #e7edf2",
              "& .MuiTab-root": {
                minHeight: 40,
                textTransform: "none",
                fontSize: ajustarTamanhoFonteSre(12),
              },
            }}
          >
            <Tab
              value="geral"
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                  }}
                >
                  Para todos
                  <Box
                    component="span"
                    sx={{
                      px: 0.8,
                      borderRadius: 3,
                      bgcolor: "#e8f3fc",
                      color: azul,
                      fontWeight: 700,
                    }}
                  >
                    {paraTodos}
                  </Box>
                </Box>
              }
            />

            <Tab
              value="individual"
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                  }}
                >
                  Para você
                  <Box
                    component="span"
                    sx={{
                      px: 0.8,
                      borderRadius: 3,
                      bgcolor: "#e8f3fc",
                      color: azul,
                      fontWeight: 700,
                    }}
                  >
                    {paraMim}
                  </Box>
                </Box>
              }
            />
          </Tabs>
        )}

        {error && (
          <Typography
            color="error"
            variant="caption"
            sx={{
              display: "block",
              px: 2,
              py: 1,
            }}
          >
            {error}
          </Typography>
        )}

        <Box
          sx={{
            maxHeight: "min(65vh, 490px)",
            overflowY: "auto",
            px: 1,
            py: 1,
          }}
        >
          {loading && notifications.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                p: 2,
              }}
            >
              <CircularProgress size={22} />
            </Box>
          ) : (
            <>
              <Button
                fullWidth
                onClick={() => {
                  setNaoLidasAbertas((anteriores) => ({
                    ...anteriores,
                    [aba]: !anteriores[aba],
                  }));
                }}
                aria-expanded={naoLidasAberto}
                startIcon={<NotificationsNoneIcon />}
                endIcon={
                  <ExpandMoreIcon
                    sx={{
                      transform: naoLidasAberto ? "rotate(180deg)" : "none",
                      transition: "transform .2s",
                    }}
                  />
                }
                sx={{
                  mb: 0.8,
                  justifyContent: "space-between",
                  textTransform: "none",
                  color: "#263f52",
                  fontWeight: 700,
                  border: "1px solid #e5ebf0",
                  borderRadius: 1.5,
                }}
              >
                Não lidas ({atuais.length})
              </Button>

              <Collapse in={naoLidasAberto}>
                {atuais.length > 0 ? (
                  atuais.map((item) => renderizarItem(item))
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      px: 1,
                      py: 1.5,
                      color: "text.secondary",
                    }}
                  >
                    {exibirAbas
                      ? "Nenhuma notificação nova nesta aba."
                      : "Nenhuma notificação nova."}
                  </Typography>
                )}
              </Collapse>

              {historico.length > 0 && (
                <>
                  <Button
                    fullWidth
                    onClick={() => setHistoricoAberto((anterior) => !anterior)}
                    aria-expanded={historicoAberto}
                    startIcon={<HistoryIcon />}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          transform: historicoAberto
                            ? "rotate(180deg)"
                            : "none",
                          transition: "transform .2s",
                        }}
                      />
                    }
                    sx={{
                      mt: 1,
                      mb: 0.6,
                      justifyContent: "space-between",
                      textTransform: "none",
                      color: "#263f52",
                      fontWeight: 700,
                      border: "1px solid #e5ebf0",
                      borderRadius: 1.5,
                    }}
                  >
                    Histórico ({historico.length})
                  </Button>

                  <Collapse in={historicoAberto}>
                    {historico.map((item) => renderizarItem(item, true))}
                  </Collapse>
                </>
              )}
            </>
          )}
        </Box>

        {notifications.length > 0 && onClear && (
          <Box
            sx={{
              borderTop: "1px solid #e7edf2",
              textAlign: "center",
              py: 0.35,
            }}
          >
            <Button
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={onClear}
              sx={{
                textTransform: "none",
                fontSize: ajustarTamanhoFonteSre(11),
                color: azul,
              }}
            >
              Limpar notificações
            </Button>
          </Box>
        )}
      </Menu>
    </>
  );
}
